import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SurveysService, type Question, type QuestionCreate, type QuestionUpdate } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

export const useQuestions = () =>
  useQuery({
    queryKey: ["questions"],
    queryFn: () => fetchAll<Question>(SurveysService.getApiQuestions),
    staleTime: 5 * 60_000,
  });

export const useCreateQuestion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: QuestionCreate) => {
      const res = await SurveysService.postApiQuestions({ body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as Question;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions"] }),
  });
};

export const useUpdateQuestion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: QuestionUpdate }) => {
      const res = await SurveysService.putApiQuestionsById({ path: { id }, body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as Question;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions"] }),
  });
};

export const useDeleteQuestion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await SurveysService.deleteApiQuestionsById({ path: { id }, headers: getAuthHeaders() });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions"] }),
  });
};
